import { cn } from '@/lib/utils';

/**
 * Fallback placeholder used in product image slots when the product has no
 * image (admin hasn't uploaded one yet, or the URL is broken).
 *
 * Shows the tenant's brand logo (from the `--brand-logo` CSS variable set on
 * `:root` by the root layout), centered, grayscaled, and dimmed — so it
 * reads as "this is intentionally a placeholder", not as "the brand wanted
 * the logo here at full strength". If the tenant has no logo, the wrapper
 * collapses to a clean neutral surface.
 *
 * Pass-through props:
 *   - `fill`  — when used inside a `position: relative` slot (most product
 *               cards / galleries do `aspect-square relative`), the
 *               placeholder positions itself absolutely to cover the slot.
 *   - `className` — extra styles, e.g. corner rounding.
 */
type Props = {
  /** Absolute-position the placeholder to fill its parent (parent must be `relative`). */
  fill?: boolean;
  className?: string;
  /** Accessible label — usually the product name. */
  alt?: string;
};

export function ProductImagePlaceholder({
  fill = true,
  className,
  alt = '',
}: Props) {
  return (
    <div
      role="img"
      aria-label={alt}
      className={cn(
        'bg-subtle brand-logo-placeholder',
        fill ? 'absolute inset-0' : 'w-full h-full',
        className,
      )}
    />
  );
}
